/**
 * Runs `work` over `items` with at most `limit` in flight at once, in
 * original order. What Stage 5's export needs and `Promise.all` does not: a
 * phone on real signal fetching a few hundred photos should not open a few
 * hundred sockets together, and the export screen's progress line needs
 * something to call after each one finishes rather than only at the very end.
 */
export async function pooled<T, R>(
  items: readonly T[],
  limit: number,
  work: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array<R>(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next++;
      // Safe: `index` was just claimed from `next` while it was still less
      // than `items.length`, so it addresses a real element.
      results[index] = await work(items[index]!);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}
